import { action, computed, observable } from 'mobx'
import { observer } from 'mobx-react'
import React, { Component, Fragment } from 'react'
import styled from 'styled-components'
import Button from '../components/Button'
import Card from '../components/Card'
import Paragraph from '../components/Paragraph'
import Prompts from '../components/Prompts'
import Row from '../components/Row'
import Switcher from '../components/Switcher'
import Text from '../components/Text'
import patient from '../stores/patient'
import getStoryPatientName from '../utils/getStoryPatientName'

const Audio = styled.audio`
  display: block;
  width: 100%;
  margin: 0 0 1.5rem 0;
`

const Link = styled.button`
  background: transparent;
  cursor: pointer;
  color: ${props => props.active ? 'var(--red)' : 'var(--gray)'};
  text-decoration: underline;
  text-align: center;
  display: block;
  width: 100%;
  padding: 0.25rem 0;
  font-weight: ${props => props.active ? '500' : '400'};
`

@observer
export default class Stories extends Component {
  @observable scene = 'VALUES'
  @observable story = 'UNCERTAINTY'
  @observable scriptShown = false

  @computed
  get audioSrc() {
    let suffix

    switch (this.story) {
      case 'CANT_REMEMBER':
        suffix = `-doctorGender-${patient.fields.doctorGender}-usesSmartphone-${patient.fields.usesSmartphone ? 'YES' : 'NO'}`
        break;
      case 'NOT_ALWAYS_HOME':
        suffix = `-usesSmartphone-${patient.fields.usesSmartphone ? 'YES' : 'NO'}`
        break;
      case 'UNCERTAINTY':
        suffix = `-usesInternet-${patient.fields.usesInternet ? 'YES' : 'NO'}-doctorGender-${patient.fields.doctorGender}`
        break;
      case 'USING_EYE_DROPS':
        suffix = `-socialSupport-${patient.fields.socialSupport}`
        break;
      default:
        suffix = ''
    }

    const ethnicity = patient.fields.ethnicity === 'BLACK_AFRICAN_AMERICAN' ? 'BLACK' : 'WHITE'
    return `/audios/${this.story}/ethnicity-${ethnicity}-gender-${patient.fields.gender}${suffix}.mp3`
  }
  
  @computed
  get storyLinks() {
    switch (this.scene) {
      case 'VALUES':
        return ['UNCERTAINTY', 'CONFIDENCE', 'UNDERSTANDING']
      case 'MEDICATIONS':
        return ['SIDE_EFFECTS', 'USING_EYE_DROPS', 'EXPENSE', 'ADDICTION']
      case 'HABITS':
        return ['CANT_REMEMBER', 'STRESS', 'NOT_ALWAYS_HOME']
      case 'COMFORT':
        return ['FAMILY', 'USING_IN_PUBLIC', 'TRUSTING_MY_DOCTOR']
      default:
        throw new Error('invalid story while computing story links')
    }
  }

  @action
  setScene = scene => {
    this.scene = scene
    this.story = this.storyLinks[0]
  }

  @action
  setStory = story => {
    this.story = story
  }

  @action
  toggleScript = () => {
    this.scriptShown = !this.scriptShown
  }

  render() {
    const storyPatientName = getStoryPatientName()
    const { hidePrompts } = this.props

    return (
      <Card>
        <Card.Pad>
          <Audio src={this.audioSrc} preload="true" controls />
          <Switcher
            color="red"
            choices="storyScene"
            value={this.scene}
            onChange={this.setScene}
          />
          <Row>
            {this.storyLinks.map((v, i) => (
              <Row.Cell key={i}>
                <Link active={this.story === v} onClick={() => this.setStory(v)}>
                  <Text str={`choices.story.${v}`} />
                </Link>
              </Row.Cell>
            ))}
          </Row>
        </Card.Pad>
        <Card.Divider />
        <Card.Pad>
          {!hidePrompts &&
            <Prompts withBottomMargin>
              <Prompts.Q small str="prose.howCouldImagineStories" inserts={{ name: storyPatientName }} />
              <Prompts.R small str="prose.itSoundsLikeStoryExample" />
              <Prompts.P small str="prose.anotherThingYouMentioned" inserts={{ name: storyPatientName }} />
            </Prompts>
          }
          <Button color="red" onClick={this.toggleScript}>
            <i
              className={this.scriptShown ? 'fas fa-times' : 'fas fa-file-alt'}
              style={{ marginRight: '0.5rem' }}
            />
            <Text str={this.scriptShown ? 'titles.hideScript' : 'titles.showScript'} />
          </Button>
          {this.scriptShown &&
            <Fragment>
              {/* UNCERTAINTY */}
              {this.story === 'UNCERTAINTY' &&
                <Fragment>
                  <Paragraph str="prose.sometimesISkip" />
                  <Paragraph str="prose.wasntConvinced" />
                  {patient.fields.usesInternet &&
                    <Paragraph str="prose.lookedOnline" />
                  }
                  {!patient.fields.usesInternet &&
                    <Paragraph str="prose.didSomeResearch" />
                  }
                  {patient.fields.doctorGender === 'FEMALE' &&
                    <Paragraph str="prose.talkedWithDoctorFemale" />
                  }
                  {patient.fields.doctorGender === 'MALE' &&
                    <Paragraph str="prose.talkedWithDoctorMale" />
                  }
                </Fragment>
              }
              {/* CONFIDENCE */}
              {this.story === 'CONFIDENCE' &&
                <Fragment>
                  <Paragraph str="prose.dontAlwaysFeel" />
                  <Paragraph str="prose.butWhenIThink" />
                  <Paragraph str="prose.whenImNot" />
                </Fragment>
              }
              {/* UNDERSTANDING */}
              {this.story === 'UNDERSTANDING' &&
                <Fragment>
                  <Paragraph str="prose.didntUnderstandHow" />
                  <Paragraph str="prose.soFarHaveLearned" />
                  <Paragraph str="prose.somePeoplesOpticNerve" />
                  <Paragraph str="prose.afterLearningMore" />
                </Fragment>
              }
              {/* SIDE_EFFECTS */}
              {this.story === 'SIDE_EFFECTS' &&
                <Fragment>
                  <Paragraph str="prose.wantedToStop" />
                  {patient.fields.gender === 'FEMALE' &&
                    <Paragraph str="prose.myFriendHadBeenFemale" />
                  }
                  {patient.fields.gender === 'MALE' &&
                    <Paragraph str="prose.myFriendHadBeenMale" />
                  }
                  <Paragraph str="prose.foundOut" />
                  <Paragraph str="prose.whenIWakeUp" />
                  <Paragraph str="prose.allOfThese" />
                </Fragment>
              }
              {/* USING_EYE_DROPS */}
              {this.story === 'USING_EYE_DROPS' &&
                <Fragment>
                  <Paragraph str="prose.wasHaving" />
                  {/* We should probably get rid of this paragraph...or at least make it generic. */}
                  {(patient.fields.usesInternet || patient.fields.socialSupport === 'INDEPENDENT') && patient.fields.gender === 'FEMALE' &&
                    <Paragraph str="prose.askedAFriendFemale" />
                  }
                  {(patient.fields.usesInternet || patient.fields.socialSupport === 'INDEPENDENT') && patient.fields.gender === 'MALE' &&
                    <Paragraph str="prose.askedAFriendMale" />
                  }
                  {!patient.fields.usesInternet && patient.fields.socialSupport === 'SPOUSE' && patient.fields.gender === 'FEMALE' &&
                    <Paragraph str="prose.friendToldMeSpouseFemale" />
                  }
                  {!patient.fields.usesInternet && patient.fields.socialSupport === 'SPOUSE' && patient.fields.gender === 'FEMALE' &&
                    <Paragraph str="prose.friendToldMeSpouseMale" />
                  }
                  {!patient.fields.usesInternet && patient.fields.socialSupport === 'DAUGHTER' && patient.fields.gender === 'FEMALE' &&
                    <Paragraph str="prose.friendToldMeDaughterFemale" />
                  }
                  {!patient.fields.usesInternet && patient.fields.socialSupport === 'DAUGHTER' && patient.fields.gender === 'FEMALE' &&
                    <Paragraph str="prose.friendToldMeDaughterMale" />
                  }
                  {!patient.fields.usesInternet && patient.fields.socialSupport === 'SON' && patient.fields.gender === 'FEMALE' &&
                    <Paragraph str="prose.friendToldMeSonFemale" />
                  }
                  {!patient.fields.usesInternet && patient.fields.socialSupport === 'SON' && patient.fields.gender === 'FEMALE' &&
                    <Paragraph str="prose.friendToldMeSonMale" />
                  }
                  {!patient.fields.usesInternet && patient.fields.socialSupport === 'FRIEND' && patient.fields.gender === 'FEMALE' &&
                    <Paragraph str="prose.friendToldMeFriendFemale" />
                  }
                  {!patient.fields.usesInternet && patient.fields.socialSupport === 'FRIEND' && patient.fields.gender === 'FEMALE' &&
                    <Paragraph str="prose.friendToldMeFriendMale" />
                  }
                  {!patient.fields.usesInternet && patient.fields.socialSupport === 'OTHER' && patient.fields.gender === 'FEMALE' &&
                    <Paragraph str="prose.friendToldMeOtherFemale" />
                  }
                  {!patient.fields.usesInternet && patient.fields.socialSupport === 'OTHER' && patient.fields.gender === 'FEMALE' &&
                    <Paragraph str="prose.friendToldMeOtherMale" />
                  }
                </Fragment>
              }
              {/* EXPENSE */}
              {this.story === 'EXPENSE' &&
                <Fragment>
                  <Paragraph str="prose.wasHavingTroublePaying" />
                  <Paragraph str="prose.butThenIFound" />
                  <Paragraph str="prose.alsoLearned" />
                  {patient.fields.gender === 'FEMALE' &&
                    <Paragraph str="prose.oneOfMyFriendsFemale" />
                  }
                  {patient.fields.gender === 'MALE' &&
                    <Paragraph str="prose.oneOfMyFriendsMale" />
                  }
                </Fragment>
              }
              {/* ADDICTION */}
              {this.story === 'ADDICTION' &&
                <Fragment>
                  <Paragraph str="prose.whenIFirstStarted" />
                  <Paragraph str="prose.talkedWithMyDoctor" />
                  <Paragraph str="prose.onlyTakeMyDrops" />
                </Fragment>
              }
              {/* CANT_REMEMBER */}
              {this.story === 'CANT_REMEMBER' &&
                <Fragment>
                  {patient.fields.doctorGender === 'FEMALE' &&
                    <Fragment>
                      <Paragraph str="prose.atLeastAFewFemale" />
                      <Paragraph str="prose.doctorExplainedFemale" />
                    </Fragment>
                  }
                  {patient.fields.doctorGender === 'MALE' &&
                    <Fragment>
                      <Paragraph str="prose.atLeastAFewMale" />
                      <Paragraph str="prose.doctorExplainedMale" />
                    </Fragment>
                  }
                  <Paragraph str="prose.tookMeAwhile" />
                  <Paragraph str="prose.usedToRunOut" />
                  {patient.fields.usesSmartphone &&
                    <Paragraph str="prose.nowIPutYes" />
                  }
                  {!patient.fields.usesSmartphone &&
                    <Paragraph str="prose.nowIPutNo" />
                  }
                  {patient.fields.gender === 'FEMALE' &&
                    <Fragment>
                      <Paragraph str="prose.linkingMyMedicationFemale" />
                      <Paragraph str="prose.theDoseIHaveFemale" />
                    </Fragment>
                  }
                  {patient.fields.gender === 'MALE' &&
                    <Fragment>
                      <Paragraph str="prose.linkingMyMedicationMale" />
                      <Paragraph str="prose.theDoseIHaveMale" />
                    </Fragment>
                  }
                  <Paragraph str="prose.thatWayIAlways" />
                </Fragment>
              }
              {/* STRESS */}
              {this.story === 'STRESS' &&
                <Fragment>
                  {patient.fields.gender === 'FEMALE' &&
                    <Paragraph str="prose.haveSoMuchGoingFemale" />
                  }
                  {patient.fields.gender === 'MALE' &&
                    <Paragraph str="prose.haveSoMuchGoingMale" />
                  }
                  <Paragraph str="prose.foundThatTalking" />
                </Fragment>
              }
              {/* NOT_ALWAYS_HOME */}
              {this.story === 'NOT_ALWAYS_HOME' &&
                <Fragment>
                  {patient.fields.usesSmartphone && patient.fields.gender === 'FEMALE' &&
                    <Paragraph str="prose.haveTroubleRememberingYesFemale" />
                  }
                  {patient.fields.usesSmartphone && patient.fields.gender === 'MALE' &&
                    <Paragraph str="prose.haveTroubleRememberingYesMale" />
                  }
                  {!patient.fields.usesSmartphone && patient.fields.gender === 'FEMALE' &&
                    <Paragraph str="prose.haveTroubleRememberingNoFemale" />
                  }
                  {!patient.fields.usesSmartphone && patient.fields.gender === 'MALE' &&
                    <Paragraph str="prose.haveTroubleRememberingNoMale" />
                  }
                  {patient.fields.gender === 'FEMALE' &&
                    <Paragraph str="prose.oneOfFriendsTravelsFemale" />
                  }
                  {patient.fields.gender === 'MALE' &&
                    <Paragraph str="prose.oneOfFriendsTravelsMale" />
                  }
                  <Paragraph str="prose.doTheSameNow" />
                </Fragment>
              }
              {/* FAMILY */}
              {this.story === 'FAMILY' &&
                <Fragment>
                  <Paragraph str="prose.wasEmbarrassed" />
                  <Paragraph str="prose.onceIDid" />
                </Fragment>
              }
              {/* USING_IN_PUBLIC */}
              {this.story === 'USING_IN_PUBLIC' &&
                <Fragment>
                  <Paragraph str="prose.firstDiagnosed" />
                  <Paragraph str="prose.butThenIFinally" />
                </Fragment>
              }
              {/* TRUSTING_MY_DOCTOR */}
              {this.story === 'TRUSTING_MY_DOCTOR' &&
                <Fragment>
                  <Paragraph str="prose.didNotFeelMy" />
                  <Paragraph str="prose.decidedToGet" />
                </Fragment>
              }
            </Fragment>
          }
        </Card.Pad>
      </Card>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/Stories.js